import {
  AppWindow,
  ArrowRight,
  Check,
  ChevronRight,
  DollarSign,
  Recycle,
  Rocket,
  ShieldCheck,
  SquareTerminal,
  type LucideIcon,
} from "lucide-react";
import { AndroidLogo } from "./AndroidLogo";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function AndroidVsLinuxStbPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const verdictAndroid = [
    messages.avlVerdictAndroid1,
    messages.avlVerdictAndroid2,
    messages.avlVerdictAndroid3,
    messages.avlVerdictAndroid4,
  ];
  const verdictLinux = [
    messages.avlVerdictLinux1,
    messages.avlVerdictLinux2,
    messages.avlVerdictLinux3,
    messages.avlVerdictLinux4,
  ];

  const tableRows = [
    { dim: messages.avlRow1Dim, a: messages.avlRow1Android, l: messages.avlRow1Linux },
    { dim: messages.avlRow2Dim, a: messages.avlRow2Android, l: messages.avlRow2Linux },
    { dim: messages.avlRow3Dim, a: messages.avlRow3Android, l: messages.avlRow3Linux },
    { dim: messages.avlRow4Dim, a: messages.avlRow4Android, l: messages.avlRow4Linux },
    { dim: messages.avlRow5Dim, a: messages.avlRow5Android, l: messages.avlRow5Linux },
    { dim: messages.avlRow6Dim, a: messages.avlRow6Android, l: messages.avlRow6Linux },
    { dim: messages.avlRow7Dim, a: messages.avlRow7Android, l: messages.avlRow7Linux },
    { dim: messages.avlRow9Dim, a: messages.avlRow9Android, l: messages.avlRow9Linux },
    { dim: messages.avlRow10Dim, a: messages.avlRow10Android, l: messages.avlRow10Linux },
    { dim: messages.avlRow12Dim, a: messages.avlRow12Android, l: messages.avlRow12Linux },
    { dim: messages.avlRowAppDim, a: messages.avlRowAppAndroid, l: messages.avlRowAppLinux },
  ];

  const inextStack: { label: string; href?: string }[] = [
    { label: messages.avlStackAlcatraz, href: "/solutions/alcatraz-dms" },
    { label: messages.avlStackOta, href: "/solutions/ota-updates" },
    { label: messages.avlStackTr069 },
    { label: messages.avlStackCustomization, href: "/solutions/customization" },
  ];

  const decide: { icon: LucideIcon; title: string; body: string }[] = [
    { icon: AppWindow, title: messages.avlDecide1Title, body: messages.avlDecide1Body },
    { icon: DollarSign, title: messages.avlDecide2Title, body: messages.avlDecide2Body },
    { icon: ShieldCheck, title: messages.avlDecide3Title, body: messages.avlDecide3Body },
    { icon: Rocket, title: messages.avlDecide4Title, body: messages.avlDecide4Body },
    { icon: Recycle, title: messages.avlDecide5Title, body: messages.avlDecide5Body },
  ];

  const profiles: { title: string; pick: string; body: string; tone: "android" | "linux" }[] = [
    {
      title: messages.avlProfile1Title,
      pick: messages.avlProfile1Pick,
      body: messages.avlProfile1Body,
      tone: "android",
    },
    {
      title: messages.avlProfile2Title,
      pick: messages.avlProfile2Pick,
      body: messages.avlProfile2Body,
      tone: "linux",
    },
    {
      title: messages.avlProfile3Title,
      pick: messages.avlProfile3Pick,
      body: messages.avlProfile3Body,
      tone: "linux",
    },
    {
      title: messages.avlProfile4Title,
      pick: messages.avlProfile4Pick,
      body: messages.avlProfile4Body,
      tone: "linux",
    },
    {
      title: messages.avlProfile5Title,
      pick: messages.avlProfile5Pick,
      body: messages.avlProfile5Body,
      tone: "android",
    },
  ];

  const faqs = [
    { q: messages.avlFaqQ1, a: messages.avlFaqA1 },
    { q: messages.avlFaqQ2, a: messages.avlFaqA2 },
    { q: messages.avlFaqQ3, a: messages.avlFaqA3 },
    { q: messages.avlFaqQ4, a: messages.avlFaqA4 },
    { q: messages.avlFaqQ5, a: messages.avlFaqA5 },
  ];

  const pickBadge = (tone: "android" | "linux") =>
    tone === "android"
      ? "border-primary/20 bg-primary/10 text-primary"
      : "border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.avlBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.avlHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.avlHeroTitle} <span className="text-gradient">{messages.avlHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.avlHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.avlHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.avlHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#comparison">{messages.avlHeroCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* What we mean */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.avlDefineEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.avlDefineTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.avlDefineBody}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border border-primary/25 bg-card/70 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <AndroidLogo className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {messages.avlDefineAndroidLabel}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.avlDefineAndroidBody}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-500/25 bg-card/70 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <SquareTerminal className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {messages.avlDefineLinuxLabel}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {messages.avlDefineLinuxBody}
              </p>
            </div>
          </div>
          <p className="mt-8 rounded-xl border border-border/60 bg-muted/30 px-5 py-4 text-sm leading-relaxed text-foreground/90">
            {messages.avlDefineCandid}
          </p>
        </div>
      </RevealSection>

      {/* Quick verdict */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.avlVerdictEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.avlVerdictTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-primary/25 bg-card/70 p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <AndroidLogo className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold text-foreground">
                  {messages.avlVerdictAndroidTitle}
                </h3>
              </div>
              <ul className="mt-5 space-y-3">
                {verdictAndroid.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-500/25 bg-card/70 p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <SquareTerminal className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold text-foreground">
                  {messages.avlVerdictLinuxTitle}
                </h3>
              </div>
              <ul className="mt-5 space-y-3">
                {verdictLinux.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-sm text-muted-foreground">
            {messages.avlVerdictFootnote}
          </p>
        </div>
      </RevealSection>

      {/* Core difference */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.avlCoreEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.avlCoreTitle}
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.avlCoreBody1}</p>
            <p>{messages.avlCoreBody2}</p>
            <p>{messages.avlCoreBody3}</p>
            <p className="rounded-xl border-l-2 border-primary/50 bg-muted/30 py-3 pl-4 pr-3 text-foreground/90">
              {messages.avlCoreBody4}
            </p>
          </div>
        </div>
      </RevealSection>

      {/* Comparison table */}
      <RevealSection id="comparison" className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.avlTableEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.avlTableTitle}
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border/60 bg-card/60">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.avlTableColDimension || " "}
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <AndroidLogo className="h-4 w-4 text-primary" />
                      {messages.avlTableColAndroid}
                    </span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <SquareTerminal className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      {messages.avlTableColLinux}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.dim} className="border-b border-border/60 align-top last:border-0">
                    <th
                      scope="row"
                      className="px-4 py-3 text-left font-medium text-foreground/90"
                    >
                      {r.dim}
                    </th>
                    <td className="px-4 py-3 text-muted-foreground">{r.a}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.l}</td>
                  </tr>
                ))}
                <tr className="bg-primary/[0.06]">
                  <th
                    scope="row"
                    className="px-4 py-3 text-left font-semibold text-foreground"
                  >
                    {messages.avlRowInextDim}
                  </th>
                  <td colSpan={2} className="px-4 py-3 font-medium text-foreground/90">
                    {[
                      <span key="stack-label">{messages.avlStackLabel} </span>,
                      ...inextStack.flatMap((item, idx) => {
                        const node = item.href ? (
                          <a
                            key={item.label}
                            href={u(item.href)}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <span key={item.label}>{item.label}</span>
                        );
                        return idx === 0
                          ? [node]
                          : [
                              <span
                                key={`sep-${item.label}`}
                                aria-hidden
                                className="mx-1.5 text-muted-foreground/50"
                              >
                                ·
                              </span>,
                              node,
                            ];
                      }),
                    ]}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{messages.avlTableNote}</p>
        </div>
      </RevealSection>

      {/* What actually decides it */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.avlDecideEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.avlDecideTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {decide.map(({ icon: Icon, title, body }, index) => (
              <article
                key={title}
                className={`rounded-2xl border border-border/60 bg-card/60 p-6 ${
                  index === decide.length - 1 && decide.length % 2 === 1 ? "sm:col-span-2" : ""
                }`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Which is right for you */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.avlProfilesEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.avlProfilesTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {profiles.map((p) => (
              <div
                key={p.title}
                className="flex flex-col rounded-2xl border border-border/60 bg-card/70 p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold text-foreground">{p.title}</h3>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${pickBadge(
                      p.tone,
                    )}`}
                  >
                    {p.pick}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Whichever you pick — same stack */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.avlSameEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.avlSameTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            {messages.avlSameBody}
          </p>
          <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:p-8">
            <h3 className="text-lg font-semibold tracking-tight text-foreground">
              {messages.avlLockTitle}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {messages.avlLockBody}
            </p>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.avlFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.avlFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.avlCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.avlCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.avlCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.avlCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.avlCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
